import React from 'react';

import * as Yup from 'yup';
import { Redirect } from 'react-router';

import Page from '@/components/Layout/Page';
import { Form, Formik } from 'formik';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useMakeFilteredUrl } from '@/hooks/useFilter';

import { useAuthActions, useAuthState } from '@/utils/auth';
import { AddCompanyInput, useAddCompanyMutation } from '@/generated/codegen';
import FormikGotoErrorOnSubmitEffectFn from '@/components/Inputs/FormikGotoErrorOnSubmitEffect';
import useIsMounted from '@/hooks/useIsMounted';
import { makeDefaultValues } from '@/utils/makeDefaultValues';
import { LogoEditor } from '@/components/Inputs/LogoEditor';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { makeFormFieldCreator } from '@/components/Inputs/FieldCreator';
import { FormFields } from '@/components/Inputs/FormFields';
import { RolesNotificationsTable } from './RolesNotificationsTable';

const fieldCreator = makeFormFieldCreator<AddCompanyInput>();

const createCompanyFields = [
  fieldCreator({ name: 'name', label: 'Razon Social' }),
  fieldCreator({ name: 'cuit', label: 'CUIT' }),
  fieldCreator({ name: 'address', label: 'Domicilio' }),
  fieldCreator({
    name: 'phone',
    label: 'Teléfono',
    inputProps: { type: 'tel' },
  }),
  fieldCreator({ name: 'email', label: 'Email' }),
  fieldCreator({
    name: 'logo',
    label: 'Logo',
    inputComponent: LogoEditor,
  }),
];
const createFormSchema = Yup.object().shape({
  name: Yup.string().required('Campo obligatorio'),
  cuit: Yup.string()
    .matches(/^[0-9.]*$/, 'Ingresa un CUIT válido')
    .required('Campo obligatorio'),
  address: Yup.string().required('Campo obligatorio'),
  // website: Yup.string().url('Please provide a valid url'),
  phone: Yup.string().matches(/^[0-9\-\s]*$/, 'Ingresa un teléfono válido'),
  email: Yup.string().email('Ingresa un email válido'),
});

const initialValues = makeDefaultValues(createCompanyFields);

const isSingleCol = (breakpoint: string) =>
  breakpoint === 'xs' || breakpoint === 'sm' || breakpoint === 'md';

const CreateCompanyPage: React.FC = () => {
  const isMounted = useIsMounted();
  const [isCreating, setIsCreating] = React.useState(false);
  const [addCompany, { error: addCompanyError }] = useAddCompanyMutation();
  const { refreshUser } = useAuthActions();
  const { user } = useAuthState();
  const breakpoint = useBreakpoint();
  const [created, setCreated] = React.useState<number>();
  const makeFilteredUrl = useMakeFilteredUrl();
  const canCreate = user?.can_create_company;

  return (
    <Page className="@container">
      {!isCreating &&
        (created &&
        user?.roles.filter(
          (role) => role?.company?.id === created && created !== undefined
        ).length !== 0 ? (
          <Redirect to={makeFilteredUrl('', { company_id: created })} />
        ) : (
          !canCreate && !!user?.roles.length && <Redirect to={''} />
        ))}
      <div className="flex flex-col @2xl:grid @2xl:grid-cols-2 gap-4">
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Unite a una empresa</CardTitle>
            </CardHeader>
            <CardContent>
              {!user?.invitations.length && (
                <>
                  Pedile a tu administrador que te agregue a bilderit con tu
                  email: {user?.email}
                </>
              )}
              {!!user?.invitations.length && (
                <RolesNotificationsTable singleCol={isSingleCol(breakpoint)} />
              )}
            </CardContent>
          </Card>
        </div>
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Crea tu empresa</CardTitle>
            </CardHeader>
            <CardContent>
              {!canCreate && (
                <>
                  Comunicate con{' '}
                  <a href="mailto:info@bilderit.com">info@bilderit.com</a> para
                  crear tu empresa.
                </>
              )}
              {canCreate && (
                <Formik
                  initialValues={initialValues}
                  enableReinitialize
                  validationSchema={createFormSchema}
                  onSubmit={async (values, { setStatus }) => {
                    setIsCreating(true);
                    try {
                      setStatus(undefined);

                      const id = await addCompany({
                        variables: values,
                      })
                        .then(({ data, errors }) => {
                          if (errors) {
                            errors.map(console.error);
                            throw errors;
                          }
                          return data?.addCompany && 'item' in data.addCompany
                            ? data?.addCompany?.item?.id
                            : undefined;
                        })
                        .catch((e) => {
                          if (!isMounted.current) return;
                          setStatus({ error: e });
                          setIsCreating(false);
                          return undefined;
                        });

                      refreshUser()
                        .then(() => {
                          if (!isMounted.current) return;
                          setCreated(id);
                          setIsCreating(false);
                        })
                        .catch((e) => {
                          if (!isMounted.current) return;
                          setStatus({ error: e });
                          setIsCreating(false);
                        });
                    } catch (e) {
                      console.error(e);
                      if (!isMounted.current) return;
                      setStatus({ error: e });
                      setIsCreating(false);
                    }
                  }}
                >
                  {({ isSubmitting, status }) => (
                    <Form className="flex flex-col gap-4">
                      <FormFields fields={createCompanyFields} />
                      <span style={{ color: 'red' }}>
                        {status?.error?.message}
                        {addCompanyError?.message}
                      </span>
                      <Button
                        size="lg"
                        className="block text-lg"
                        disabled={isSubmitting}
                        // variant="success"
                        type="submit"
                      >
                        {isSubmitting && <LoadingSpinner />}
                        {!isSubmitting && 'CREAR!'}
                      </Button>
                      <FormikGotoErrorOnSubmitEffectFn />
                    </Form>
                  )}
                </Formik>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Page>
  );
};

export default CreateCompanyPage;
